/**
 * Extract locale prefix from the URL before Vike routes.
 * Default locale (en-US) uses bare paths; others use /{locale}/...
 * Also strips optional /clone preview basename.
 */

import type { OnBeforeRouteSync } from 'vike/types';
import { DEFAULT_LOCALE, isSupportedLocale, type SupportedLocale } from '../src/lib/intl';

function stripClonePrefix(url: string): { url: string; clone: boolean } {
  if (url === '/clone' || url.startsWith('/clone/')) {
    return { url: url.slice('/clone'.length) || '/', clone: true };
  }
  // Keep query/hash attached to pathless clone root
  if (url.startsWith('/clone?') || url.startsWith('/clone#')) {
    return { url: `/${url.slice('/clone'.length)}`, clone: true };
  }
  return { url, clone: false };
}

/**
 * CloudFront default_root_object rewrites `/` → `/index.html` before the
 * request hits ECS. Normalize so Vike filesystem routes still match home.
 */
function stripIndexHtml(pathname: string): string {
  if (pathname === '/index.html') return '/';
  if (pathname.endsWith('/index.html')) {
    const without = pathname.slice(0, -'/index.html'.length);
    return without || '/';
  }
  return pathname;
}

export const onBeforeRoute: OnBeforeRouteSync = (pageContext) => {
  let { urlOriginal } = pageContext;
  const { url: withoutClone, clone } = stripClonePrefix(urlOriginal);
  urlOriginal = withoutClone;

  // Split path from query/hash for locale parsing
  const match = urlOriginal.match(/^([^?#]*)(.*)$/);
  const pathname = stripIndexHtml(match?.[1] ?? urlOriginal);
  const suffix = match?.[2] ?? '';

  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0];

  let locale: SupportedLocale = DEFAULT_LOCALE;
  let urlLogical = pathname;

  if (first && isSupportedLocale(first) && first !== DEFAULT_LOCALE) {
    locale = first;
    const rest = segments.slice(1).join('/');
    urlLogical = rest ? `/${rest}` : '/';
  }

  return {
    pageContext: {
      locale,
      // Vike router matches against urlLogical (locale stripped)
      urlLogical: `${urlLogical}${suffix}`,
      // Preserve clone flag for client if needed later
      cloneBasename: clone,
    },
  };
};
